import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const INTEREST_OPTIONS = [
  "groceries", "travel", "credit-cards", "dining", "telecom", "subscriptions",
  "electronics", "fashion", "household", "kids", "pets", "wellness", "insurance", "software",
];
const STORE_OPTIONS = ["Walmart", "Target", "Costco", "Amazon", "Whole Foods", "Instacart", "Best Buy", "Macy's"];
const DIETARY_OPTIONS = ["Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-Free"];
const CC_OPTIONS = ["Chase Sapphire", "Amex Gold", "Capital One Venture", "Citi Double Cash", "Discover It", "None"];

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    household_size: 2,
    monthly_grocery: 600,
    travel_frequency: "occasional",
    favorite_stores: [],
    interests: [],
    dietary: [],
    has_pets: false,
    has_kids: false,
    credit_cards: [],
    subscriptions_paid: 50,
    budget_goal: 2000,
    city: "",
    zip_code: "",
  });

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleArr = (k, val) =>
    setData((d) => ({ ...d, [k]: d[k].includes(val) ? d[k].filter((x) => x !== val) : [...d[k], val] }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/onboarding", data);
      await refreshUser();
      toast.success("Profile saved! Generating your AI deal feed…");
      nav("/app");
    } catch (e) {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="font-display font-bold tracking-tight">LoyaltyCircle AI</span>
        </div>

        <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
          <span data-testid="onboarding-step">Step {step} of 4</span>
          <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full bg-emerald-700 transition-all" style={{ width: `${(step / 4) * 100}%` }} />
          </div>
        </div>

        <Card className="border-gray-200 shadow-none">
          <CardContent className="p-8">
            {step === 1 && (
              <div data-testid="onboarding-step-1">
                <h2 className="font-display font-bold text-2xl tracking-tight">Tell us about your household</h2>
                <p className="text-gray-600 mt-1">Helps Ria recommend deals tailored to your family.</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Household size</Label>
                    <Input type="number" min="1" max="20" value={data.household_size} onChange={(e) => update("household_size", parseInt(e.target.value || "1"))} data-testid="ob-household" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Monthly grocery spend ($)</Label>
                    <Input type="number" min="0" step="50" value={data.monthly_grocery} onChange={(e) => update("monthly_grocery", parseFloat(e.target.value || "0"))} data-testid="ob-grocery" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">City</Label>
                    <Input value={data.city} onChange={(e) => update("city", e.target.value)} placeholder="Dallas" data-testid="ob-city" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">ZIP code</Label>
                    <Input value={data.zip_code} onChange={(e) => update("zip_code", e.target.value)} placeholder="75201" data-testid="ob-zip" />
                  </div>
                </div>
                <div className="mt-6 flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={data.has_kids} onCheckedChange={(v) => update("has_kids", !!v)} data-testid="ob-kids" /> Has kids
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={data.has_pets} onCheckedChange={(v) => update("has_pets", !!v)} data-testid="ob-pets" /> Has pets
                  </label>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="onboarding-step-2">
                <h2 className="font-display font-bold text-2xl tracking-tight">What categories interest you?</h2>
                <p className="text-gray-600 mt-1">Pick anything that fits your life.</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => toggleArr("interests", opt)}
                      data-testid={`ob-interest-${opt}`}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                        data.interests.includes(opt)
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-gray-700 border-gray-300 hover:border-emerald-700"
                      }`}
                    >
                      {opt.replace(/-/g, " ")}
                    </button>
                  ))}
                </div>

                <h3 className="font-display font-semibold text-lg mt-8">Favorite stores</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STORE_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleArr("favorite_stores", s)}
                      data-testid={`ob-store-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                        data.favorite_stores.includes(s)
                          ? "bg-sky-600 text-white border-sky-600"
                          : "bg-white text-gray-700 border-gray-300 hover:border-sky-600"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                <h3 className="font-display font-semibold text-lg mt-8">Dietary preferences (optional)</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {DIETARY_OPTIONS.map((d) => (
                    <button
                      type="button"
                      key={d}
                      onClick={() => toggleArr("dietary", d)}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                        data.dietary.includes(d)
                          ? "bg-amber-600 text-white border-amber-600"
                          : "bg-white text-gray-700 border-gray-300 hover:border-amber-600"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="onboarding-step-3">
                <h2 className="font-display font-bold text-2xl tracking-tight">Travel & spending habits</h2>
                <div className="mt-6">
                  <Label className="mb-2 block">How often do you travel?</Label>
                  <div className="flex gap-2">
                    {["rare", "occasional", "frequent"].map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => update("travel_frequency", f)}
                        data-testid={`ob-travel-${f}`}
                        className={`flex-1 px-4 py-3 rounded-xl border text-sm capitalize ${
                          data.travel_frequency === f
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <Label className="mb-2 block">Credit cards you currently use</Label>
                  <div className="flex flex-wrap gap-2">
                    {CC_OPTIONS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => toggleArr("credit_cards", c)}
                        className={`px-4 py-2 rounded-full border text-sm font-medium ${
                          data.credit_cards.includes(c)
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Monthly subscription spend ($)</Label>
                    <Input type="number" min="0" step="10" value={data.subscriptions_paid} onChange={(e) => update("subscriptions_paid", parseFloat(e.target.value || "0"))} data-testid="ob-subspend" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Annual savings goal ($)</Label>
                    <Input type="number" min="0" step="100" value={data.budget_goal} onChange={(e) => update("budget_goal", parseFloat(e.target.value || "0"))} data-testid="ob-goal" />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="onboarding-step-4">
                <h2 className="font-display font-bold text-2xl tracking-tight">Looking good!</h2>
                <p className="text-gray-600 mt-1">Quick review before we generate your AI deal feed.</p>
                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Household</span><span className="font-medium">{data.household_size} people · {data.has_kids ? "kids" : "no kids"} · {data.has_pets ? "pets" : "no pets"}</span></div>
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Monthly grocery</span><span className="font-medium">${data.monthly_grocery}</span></div>
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">City</span><span className="font-medium">{data.city || "—"}</span></div>
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Travel</span><span className="font-medium capitalize">{data.travel_frequency}</span></div>
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Interests</span><span className="font-medium">{data.interests.length} picked</span></div>
                  <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Favorite stores</span><span className="font-medium">{data.favorite_stores.length} picked</span></div>
                </div>
              </div>
            )}

            <div className="mt-10 flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                data-testid="ob-back"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              {step < 4 ? (
                <Button
                  onClick={() => setStep((s) => Math.min(4, s + 1))}
                  data-testid="ob-next"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="ob-finish"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6"
                >
                  {submitting ? "Generating…" : "Finish & see my feed"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <button
          onClick={async () => { setSubmitting(true); try { await api.post("/auth/onboarding", data); await refreshUser(); nav("/app"); } finally { setSubmitting(false); } }}
          className="text-sm text-gray-500 hover:text-gray-700 mt-4"
          data-testid="ob-skip"
        >
          Skip for now (use defaults)
        </button>
      </div>
    </div>
  );
}
